import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { toast } from 'sonner';
import {
  Users,
  Calendar,
  Star,
  TrendingUp,
  Clock,
  FileText,
  Video,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Activity,
  ChevronRight,
  DollarSign,
  MessageSquare
} from 'lucide-react';

export default function DoctorHome() {
  const navigate = useNavigate();
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPatients: 0,
    pendingInquiries: 0,
    todayAppointments: 0,
    rating: 0,
    totalReviews: 0,
    quotesAccepted: 0,
    responseRate: 0
  });
  const [recentInquiries, setRecentInquiries] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load from API
      const [profileRes, requestsRes, quotesRes] = await Promise.all([
        api.get('/doctor/profile').catch(() => ({ data: { profile: {} } })),
        api.get('/doctor/treatment-requests').catch(() => ({ data: { requests: [] } })),
        api.get('/doctor/quotes').catch(() => ({ data: { quotes: [] } }))
      ]);

      const requests = requestsRes.data.requests || [];
      const quotes = quotesRes.data.quotes || [];
      const profile = profileRes.data.profile || {};

      setStats({
        totalPatients: profile.total_patients || 0,
        pendingInquiries: requests.length,
        todayAppointments: 0,
        rating: profile.rating || 0,
        totalReviews: profile.total_reviews || 0,
        quotesAccepted: quotes.filter(q => q.status === 'accepted').length,
        responseRate: profile.response_rate || 0
      });

      // Map inquiries from API
      setRecentInquiries(requests.slice(0, 3).map((r) => ({
        id: r._id,
        patient: r.patient?.display_name || 'Patient',
        condition: r.condition || r.preferred_specialization || 'Consultation',
        urgency: r.urgency || 'Medium',
        time: r.created_at ? new Date(r.created_at).toLocaleDateString() : '',
        country: r.patient?.country || 'International'
      })));

      setRecentActivity([
        { id: 1, type: 'quote_accepted', message: 'A patient accepted your quote', time: '1 hour ago' },
        { id: 2, type: 'review', message: 'New 5-star review received', time: '3 hours ago' },
        { id: 3, type: 'appointment', message: 'Appointment completed', time: '5 hours ago' },
        { id: 4, type: 'inquiry', message: 'New patient inquiry received', time: '6 hours ago' },
      ]);

    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const getUrgencyStyles = (urgency) => {
    switch (urgency) {
      case 'High': return 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800';
      case 'Medium': return 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-800';
      case 'Low': return 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800';
      default: return 'bg-slate-50 dark:bg-slate-800 text-slate-700 dark:text-slate-400';
    }
  };

  const getActivityIcon = (type) => {
    switch (type) {
      case 'quote_accepted': return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      case 'review': return <Star className="w-4 h-4 text-amber-500" />;
      case 'appointment': return <Calendar className="w-4 h-4 text-sky-500" />;
      case 'inquiry': return <MessageSquare className="w-4 h-4 text-purple-500" />;
      default: return <Activity className="w-4 h-4 text-slate-500" />;
    }
  };

  // Loading skeleton
  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-24 bg-slate-200 dark:bg-slate-800 rounded-2xl" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-28 bg-slate-200 dark:bg-slate-800 rounded-xl" />
          ))}
        </div>
        <div className="h-64 bg-slate-200 dark:bg-slate-800 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="doctor-dashboard">
      {/* Welcome Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-sky-500 to-sky-600 dark:from-sky-600 dark:to-sky-700 p-6 text-white">
        <div className="relative z-10">
          <h1 className="font-heading text-2xl md:text-3xl font-bold">
            Welcome back, Dr. {user?.full_name?.split(' ')[0] || 'Doctor'}!
          </h1>
          <p className="mt-2 text-sky-100 text-sm md:text-base">
            Here's an overview of your practice today
          </p>
          
          {/* Quick Stats Row - Mobile Optimized */}
          <div className="mt-6 grid grid-cols-3 gap-3 md:gap-6">
            <div className="text-center md:text-left">
              <p className="text-3xl md:text-4xl font-bold">{stats.todayAppointments}</p>
              <p className="text-xs md:text-sm text-sky-200 mt-1">Today's Appointments</p>
            </div>
            <div className="text-center md:text-left">
              <p className="text-3xl md:text-4xl font-bold">{stats.pendingInquiries}</p>
              <p className="text-xs md:text-sm text-sky-200 mt-1">Pending Inquiries</p>
            </div>
            <div className="text-center md:text-left">
              <p className="text-3xl md:text-4xl font-bold">{stats.responseRate}%</p>
              <p className="text-xs md:text-sm text-sky-200 mt-1">Response Rate</p>
            </div>
          </div>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-32 translate-x-32" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-24 -translate-x-24" />
      </div>

      {/* Quick Actions - Mobile First */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <button
          onClick={() => navigate('/doctor/video-consultation')}
          className="flex flex-col items-center justify-center p-4 md:p-6 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-sky-300 dark:hover:border-sky-700 hover:shadow-lg hover:shadow-sky-500/10 transition-all group"
          data-testid="quick-action-video"
        >
          <div className="w-12 h-12 rounded-xl bg-sky-50 dark:bg-sky-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
            <Video className="w-6 h-6 text-sky-600 dark:text-sky-400" strokeWidth={1.5} />
          </div>
          <span className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-300">Start Call</span>
        </button>
        
        <button
          onClick={() => navigate('/doctor/inquiries')}
          className="flex flex-col items-center justify-center p-4 md:p-6 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-purple-300 dark:hover:border-purple-700 hover:shadow-lg hover:shadow-purple-500/10 transition-all group"
          data-testid="quick-action-inquiries"
        >
          <div className="w-12 h-12 rounded-xl bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center group-hover:scale-110 transition-transform relative">
            <MessageSquare className="w-6 h-6 text-purple-600 dark:text-purple-400" strokeWidth={1.5} />
            {stats.pendingInquiries > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {stats.pendingInquiries}
              </span>
            )}
          </div>
          <span className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-300">Inquiries</span>
        </button>
        
        <button
          onClick={() => navigate('/doctor/appointments')}
          className="flex flex-col items-center justify-center p-4 md:p-6 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-emerald-300 dark:hover:border-emerald-700 hover:shadow-lg hover:shadow-emerald-500/10 transition-all group"
          data-testid="quick-action-schedule"
        >
          <div className="w-12 h-12 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
            <Calendar className="w-6 h-6 text-emerald-600 dark:text-emerald-400" strokeWidth={1.5} />
          </div>
          <span className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-300">Schedule</span>
        </button>
        
        <button
          onClick={() => navigate('/doctor/patients')}
          className="flex flex-col items-center justify-center p-4 md:p-6 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 hover:border-amber-300 dark:hover:border-amber-700 hover:shadow-lg hover:shadow-amber-500/10 transition-all group"
          data-testid="quick-action-patients"
        >
          <div className="w-12 h-12 rounded-xl bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center group-hover:scale-110 transition-transform">
            <Users className="w-6 h-6 text-amber-600 dark:text-amber-400" strokeWidth={1.5} />
          </div>
          <span className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-300">Patients</span>
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-sky-50 dark:bg-sky-900/30 flex items-center justify-center">
              <Users className="w-5 h-5 text-sky-600 dark:text-sky-400" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.totalPatients}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Total Patients</p>
            </div>
          </div>
          <div className="mt-3 flex items-center text-xs text-emerald-600 dark:text-emerald-400">
            <TrendingUp className="w-3 h-3 mr-1" />
            +12% this month
          </div>
        </Card>
        
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 dark:bg-amber-900/30 flex items-center justify-center">
              <Star className="w-5 h-5 text-amber-500" strokeWidth={1.5} fill="currentColor" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.rating}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Rating</p>
            </div>
          </div>
          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
            Based on {stats.totalReviews} reviews
          </p>
        </Card>
        
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
              <FileText className="w-5 h-5 text-emerald-600 dark:text-emerald-400" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">{stats.quotesAccepted}%</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Quotes Accepted</p>
            </div>
          </div>
          <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
            Acceptance rate
          </p>
        </Card>
        
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-50 dark:bg-purple-900/30 flex items-center justify-center">
              <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900 dark:text-white">2.4h</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Avg Response</p>
            </div>
          </div>
          <div className="mt-3 flex items-center text-xs text-emerald-600 dark:text-emerald-400">
            <TrendingUp className="w-3 h-3 mr-1" />
            -15% from last week
          </div>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Patient Inquiries */}
        <Card className="lg:col-span-2 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <div className="p-4 md:p-6 border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading text-lg font-semibold text-slate-900 dark:text-white">Recent Patient Inquiries</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">New inquiries awaiting your response</p>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/doctor/inquiries')}
                className="text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300"
              >
                View All
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
          <div className="divide-y divide-slate-100 dark:divide-slate-800">
            {recentInquiries.map((inquiry) => (
              <div 
                key={inquiry.id} 
                className="p-4 md:p-5 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors cursor-pointer group"
                onClick={() => navigate('/doctor/inquiries')}
              >
                <div className="flex items-start gap-4">
                  <Avatar className="h-11 w-11 shrink-0">
                    <AvatarFallback className="bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-medium">
                      {inquiry.patient.split(' ').map(n => n[0]).join('')}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-medium text-slate-900 dark:text-white truncate">
                        {inquiry.patient}
                      </h3>
                      <Badge className={`shrink-0 ${getUrgencyStyles(inquiry.urgency)}`}>
                        {inquiry.urgency}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-0.5">{inquiry.condition}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-500 dark:text-slate-400">
                      <span>{inquiry.country}</span>
                      <span>•</span>
                      <span>{inquiry.time}</span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-300 dark:text-slate-600 group-hover:text-slate-500 dark:group-hover:text-slate-400 shrink-0" />
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Recent Activity */}
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <div className="p-4 md:p-6 border-b border-slate-200 dark:border-slate-800">
            <h2 className="font-heading text-lg font-semibold text-slate-900 dark:text-white">Recent Activity</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Latest updates</p>
          </div>
          <div className="p-4 md:p-6 space-y-4">
            {recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0">
                  {getActivityIcon(activity.type)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-700 dark:text-slate-300">{activity.message}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{activity.time}</p>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
